import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, DatePicker, Input, InputNumber, Modal, Segmented, Select, Spin, Switch, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { CheckOutlined, CloseOutlined, PlusOutlined, SearchOutlined, StopOutlined, EditOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import usePermissions from '../../hooks/usePermissions';
import {
  NdoePage, NdoePageHeader, NdoeActionButton, NdoeImage, NdoeEmpty, NdoeLoading, NdoeError,
  messageErreur,
} from '../../components/ndoe';
import './promotions.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Prix promotionnels — encodage « 3 gestes » (modèle → cible → remise) et
// validation admin (groupée ou individuelle, refus motivé). Spec 15/07 :
// 3 types (pourcentage / montant / prix fixe), dates optionnelles (aucune =
// permanente), cible = modèle entier ou groupe de variantes, une promo par
// cible de même niveau (remplacement proposé), variante > modèle à la résolution.
const TYPES = [
  { value: 'pourcentage', label: '% de remise' },
  { value: 'montant', label: '$ de remise' },
  { value: 'prix_fixe', label: 'Prix fixe' },
];
const STATUT_TAG = {
  en_attente: { color: 'gold', label: 'En attente' },
  active: { color: 'green', label: 'Active' },
  refusee: { color: 'red', label: 'Refusée' },
  desactivee: { color: 'default', label: 'Désactivée' },
};
const ETAT_LABEL = { permanente: 'Permanente', a_venir: 'À venir', en_cours: 'En cours', expiree: 'Expirée' };

const apercuPrix = (base, type, valeur) => {
  const b = Math.round(Number(base) || 0);
  const v = Number(valeur) || 0;
  if (b <= 0 || v <= 0) return null;
  const p = type === 'pourcentage' ? Math.round(b * (1 - v / 100))
    : type === 'montant' ? Math.round(b - v) : Math.round(v);
  return p > 0 && p < b ? p : null;
};

// ── Formulaire « 3 gestes » (création / ajustement) ─────────────────────────
function FormulairePromo({ promo, onClose, onSaved }) {
  const edition = !!promo;
  const [q, setQ] = useState('');
  const [tri, setTri] = useState('sales_desc');
  // Vide = tous les modèles. Par défaut on ne cache rien : c'est à elle de
  // décider de se concentrer, pas à l'écran de décider pour elle.
  const [promoFiltre, setPromoFiltre] = useState('');
  /*
    DÉCOUVERTE ET CATÉGORIE (demande de la propriétaire, 22/08/2026).

    « Ajoute filtre derniers arrivages, dormants, jamais vendu, dernières
    ventes… et d'autres qui peuvent m'aider à repérer des paires qui ne se
    vendent pas. »

    Ces critères existaient déjà — mais comme TRIS. Un tri remonte ces paires
    en tête sans masquer le reste : pour préparer une promotion sur les
    invendus, il faut la LISTE des invendus. Ce sont maintenant de vrais
    filtres, calculés au SERVEUR pour la même raison que le filtre promo.
  */
  const [decouverte, setDecouverte] = useState('');
  const [categorie, setCategorie] = useState('');
  const [categories, setCategories] = useState([]);
  const [resultats, setResultats] = useState([]);
  // Vrai quand la liste a été coupée : on le DIT plutôt que de laisser croire
  // que c'est tout ce qui existe.
  const [tronquee, setTronquee] = useState(false);
  const [chargeModeles, setChargeModeles] = useState(false);
  // SÉLECTION MULTIPLE — « pouvoir sélectionner un ou plusieurs modèles à la
  // fois puis leur appliquer un prix. Ça doit être simple. »
  // Un Set d'id_produit ; `modeles` garde la fiche (image, prix) pour l'aperçu.
  const [lot, setLot] = useState([]);
  const [modele, setModele] = useState(edition ? {
    id_produit: promo.id_produit, nom_produit: promo.nom_produit,
    code_produit: promo.code_produit, img: promo.img, prix: promo.prix_base,
  } : null);
  const [variantes, setVariantes] = useState([]);        // variantes du modèle choisi
  const [toutModele, setToutModele] = useState(edition ? promo.nb_variantes === 0 : true);
  const [cochees, setCochees] = useState(new Set(edition ? promo.variantes.map((v) => v.id_varianteProduit) : []));
  const [type, setType] = useState(edition ? promo.type : 'pourcentage');
  const [valeur, setValeur] = useState(edition ? promo.valeur : null);
  const [libelle, setLibelle] = useState(edition ? promo.libelle : '');
  const [permanente, setPermanente] = useState(edition ? !promo.date_debut && !promo.date_fin : true);
  const [debut, setDebut] = useState(edition && promo.date_debut ? dayjs(promo.date_debut) : null);
  const [fin, setFin] = useState(edition && promo.date_fin ? dayjs(promo.date_fin) : null);
  const [envoi, setEnvoi] = useState(false);

  // Geste 1 — PARCOURIR le catalogue (31/07/2026).
  //
  // Demande du propriétaire : « il me faut la recherche catalogue aussi avec
  // les tris avancés (meilleures ventes, arrivages…) ».
  //
  // L'écran n'affichait RIEN tant qu'on n'avait pas tapé : pour mettre un modèle
  // en promotion, il fallait déjà savoir lequel. Or la question qu'on se pose en
  // arrivant est justement « lequel mettre en promo » — et la réponse est dans
  // les chiffres : ce qui se vend bien, ce qui dort, ce qui vient d'arriver.
  //
  // Ces tris existaient DÉJÀ côté API (catalogStock.service.js) et n'étaient
  // simplement pas offerts ici. Aucun code serveur ajouté.
  useEffect(() => {
    if (edition) { setResultats([]); return undefined; }
    let alive = true;
    const t = setTimeout(async () => {
      setChargeModeles(true);
      try {
        const p = new URLSearchParams({ limit: '300', sort: tri });
        if (q.trim()) p.set('q', q.trim());
        /*
          LE FILTRE PROMO PART AU SERVEUR, ET C'EST OBLIGATOIRE.

          Demande de la propriétaire (21/08/2026) : « pouvoir filtrer les paires
          déjà en promotion de sorte que je ne travaille que sur ce qui n'est pas
          encore en promo ».

          ⚠ Filtrer ICI ne marcherait pas : on ne charge que 300 variantes, dont
          on ne garde que 60 modèles. Écarter les modèles en promo APRÈS cette
          coupe donnerait une liste presque vide et laisserait croire qu'il ne
          reste rien à mettre en promo. Un filtre qui ment par omission est pire
          que pas de filtre du tout.
        */
        if (promoFiltre) p.set('promo', promoFiltre);
        // Mêmes raisons que le filtre promo : la coupe à 300 variantes rendrait
        // tout filtrage local silencieusement incomplet.
        if (decouverte) p.set('decouverte', decouverte);
        if (categorie) p.set('id_categorie', categorie);
        const { data } = await axios.get(`${DOMAIN}/api/catalogue/stock?${p.toString()}`);
        if (!alive) return;
        // Les variantes arrivent DÉJÀ triées : on regroupe par modèle en
        // conservant l'ordre d'arrivée (un Map le garantit, un objet non).
        const parId = new Map();
        (data?.variantes || []).forEach((v) => {
          const e = parId.get(v.id_produit);
          if (!e) {
            parId.set(v.id_produit, {
              id_produit: v.id_produit, nom_produit: v.nom_produit, code_produit: v.code_produit,
              img: v.img, prix: v.prix, dispo: Number(v.disponible) || 0,
              vendu90: Number(v.vendu_90) || 0,
              // L'état sert à VOIR ce que le filtre a fait — sans lui, on ne peut
              // pas distinguer « aucune promo » de « promo finie ».
              promoEtat: v.promo_etat || null,
            });
          } else {
            if (!e.img && v.img) e.img = v.img;
            e.dispo += Number(v.disponible) || 0;   // le stock d'un modèle = celui de ses variantes
          }
        });
        const tous = [...parId.values()];
        setTronquee(tous.length > 60);
        setResultats(tous.slice(0, 60));
      } catch (e) { /* silencieux : la recherche réessaie à la frappe */ }
      finally { if (alive) setChargeModeles(false); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, tri, promoFiltre, decouverte, categorie, edition]);

  // Les catégories, une seule fois : la liste ne bouge pas pendant que la
  // propriétaire prépare une promotion.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/produit/categorie`);
        if (alive) setCategories(Array.isArray(data) ? data : []);
      } catch (e) { /* additif : sans catégories, le reste de l'écran marche */ }
    })();
    return () => { alive = false; };
  }, []);

  // Geste 2 — variantes du modèle choisi (groupées par couleur).
  useEffect(() => {
    if (!modele) { setVariantes([]); return undefined; }
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/catalogue/stock?id_produit=${modele.id_produit}&limit=1000`);
        if (alive) setVariantes(data?.variantes || []);
      } catch (e) {
        // Une panne n'est pas « ce modèle n'a aucune variante » : sans message,
        // on croit le modèle vide et on renonce à lui poser une promotion.
        if (alive) { setVariantes([]); toast.error(messageErreur(e, 'les variantes de ce modèle')); }
      }
    })();
    return () => { alive = false; };
  }, [modele]);

  const parCouleur = useMemo(() => {
    const g = {};
    variantes.forEach((v) => {
      const c = v.couleur || 'Sans couleur';
      (g[c] = g[c] || []).push(v);
    });
    return g;
  }, [variantes]);

  const basculerVariante = (id) => setCochees((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const cocherGroupe = (liste, on) => setCochees((s) => {
    const n = new Set(s); liste.forEach((v) => (on ? n.add(v.id_varianteProduit) : n.delete(v.id_varianteProduit))); return n;
  });

  const prixPromo = modele ? apercuPrix(modele.prix, type, valeur) : null;

  const basculerLot = (m) => setLot((l) => (l.some((x) => x.id_produit === m.id_produit)
    ? l.filter((x) => x.id_produit !== m.id_produit)
    : [...l, m]));

  // Aperçu du LOT : chaque modèle a son propre prix de base, donc son propre
  // prix promo. Un « -20 % » ne donne pas le même montant sur 90 $ et sur 150 $ ;
  // il faut le voir AVANT de valider, modèle par modèle.
  const apercuLot = useMemo(() => lot.map((m) => ({
    ...m, promo: apercuPrix(m.prix, type, valeur),
  })), [lot, type, valeur]);
  const lotInvalide = apercuLot.filter((m) => m.promo == null);

  // Création EN LOT : une promotion par modèle. Volontairement N appels et non
  // un endpoint « bulk » — chaque promotion reste une ligne autonome, qu'on peut
  // ensuite ajuster ou arrêter séparément. C'est aussi ce que fait déjà l'écran
  // pour une seule ; rien de nouveau à maintenir côté serveur.
  const enregistrerLot = async (remplacer = false) => {
    if (!valeur) { toast.info('Indiquez la valeur de la remise.'); return; }
    if (lotInvalide.length) {
      toast.info(`${lotInvalide.length} modèle(s) hors limites : le prix promo doit rester entre 1 $ et le prix actuel.`);
      return;
    }
    if (!permanente && (!debut || !fin)) { toast.info('Renseignez les dates, ou repassez en « Permanente ».'); return; }
    setEnvoi(true);
    const conflits = [];
    let ok = 0;
    for (const m of lot) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${DOMAIN}/api/promotions`, {
          libelle: libelle || undefined, type, valeur,
          id_produit: m.id_produit,
          variantes: [],                    // tout le modèle : c'est le sens d'une promo en lot
          date_debut: permanente ? null : debut.format('YYYY-MM-DD'),
          date_fin: permanente ? null : fin.format('YYYY-MM-DD'),
          remplacer,
        });
        ok += 1;
      } catch (e) {
        // Un modèle déjà en promo ne doit pas faire échouer les autres : on le
        // met de côté et on propose de remplacer, une fois, pour tout le reste.
        if (e.response?.status === 409) conflits.push(m);
        else toast.error(`${m.nom_produit} : ${e.response?.data?.error || 'échec'}`);
      }
    }
    setEnvoi(false);
    if (ok) toast.success(`${ok} promotion${ok > 1 ? 's' : ''} créée${ok > 1 ? 's' : ''}.`);
    if (conflits.length) {
      Modal.confirm({
        title: `${conflits.length} modèle(s) ont déjà une promotion`,
        content: `${conflits.map((c) => c.nom_produit).join(', ')}. Remplacer la promotion existante ?`,
        okText: 'Remplacer', cancelText: 'Laisser en place',
        onOk: async () => {
          setEnvoi(true);
          let ok2 = 0;
          for (const m of conflits) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await axios.post(`${DOMAIN}/api/promotions`, {
                libelle: libelle || undefined, type, valeur, id_produit: m.id_produit, variantes: [],
                date_debut: permanente ? null : debut.format('YYYY-MM-DD'),
                date_fin: permanente ? null : fin.format('YYYY-MM-DD'),
                remplacer: true,
              });
              ok2 += 1;
            } catch (e) { toast.error(`${m.nom_produit} : ${e.response?.data?.error || 'échec'}`); }
          }
          setEnvoi(false);
          if (ok2) toast.success(`${ok2} promotion(s) remplacée(s).`);
          onSaved();
        },
        onCancel: () => { if (ok) onSaved(); },
      });
      return;
    }
    if (ok) onSaved();
  };

  const enregistrer = async (remplacer = false) => {
    if (!modele) { toast.info('Choisissez d\'abord un modèle.'); return; }
    if (!toutModele && cochees.size === 0) { toast.info('Cochez au moins une variante (ou « Tout le modèle »).'); return; }
    if (!prixPromo) { toast.info('Valeur invalide : le prix promo doit rester entre 1 $ et le prix actuel.'); return; }
    if (!permanente && (!debut || !fin)) { toast.info('Renseignez les dates, ou repassez en « Permanente ».'); return; }
    setEnvoi(true);
    try {
      const corps = {
        libelle: libelle || undefined, type, valeur,
        id_produit: modele.id_produit,
        variantes: toutModele ? [] : [...cochees],
        date_debut: permanente ? null : debut.format('YYYY-MM-DD'),
        date_fin: permanente ? null : fin.format('YYYY-MM-DD'),
        remplacer,
      };
      const r = edition
        ? await axios.put(`${DOMAIN}/api/promotions/${promo.id_promotion}`, corps)
        : await axios.post(`${DOMAIN}/api/promotions`, corps);
      toast.success(r.data?.message || 'Enregistré.');
      onSaved();
    } catch (e) {
      if (e.response?.status === 409) {
        Modal.confirm({
          title: 'Une promotion vise déjà cette cible',
          content: `${e.response.data?.error || ''} Voulez-vous la remplacer ?`,
          okText: 'Remplacer', cancelText: 'Annuler',
          onOk: () => enregistrer(true),
        });
      } else {
        toast.error(e.response?.data?.error || 'Enregistrement impossible.');
      }
    } finally { setEnvoi(false); }
  };

  return (
    <Modal open onCancel={onClose} footer={null} width={720} destroyOnClose
      title={edition ? `Ajuster — ${promo.libelle}` : 'Nouvelle promotion'}>
      {/* Geste 1 : choisir — un modèle, ou plusieurs d'un coup */}
      {!modele ? (
        <div className="promo-geste">
          <b>1. Choisir {lot.length > 0 ? `— ${lot.length} modèle${lot.length > 1 ? 's' : ''} sélectionné${lot.length > 1 ? 's' : ''}` : 'le ou les modèles'}</b>
          <Input autoFocus allowClear prefix={<SearchOutlined />} placeholder="Nom ou référence… (laisser vide pour parcourir)"
            value={q} onChange={(e) => setQ(e.target.value)} />
          {/* Les tris qui répondent à « lequel mettre en promo ». Ils existaient
              déjà côté API et n'étaient pas offerts ici. */}
          <Select
            className="promo-tri" value={tri} onChange={setTri} size="small"
            options={[
              { value: 'sales_desc', label: '🔥 Meilleures ventes (90 j)' },
              { value: 'last_receipt', label: '📦 Arrivages récents' },
              { value: 'dormant_stock', label: '💤 Dort en rayon (invendu 90 j)' },
              { value: 'never_sold', label: '🧊 Jamais vendu' },
              { value: 'restock', label: '⚠️ À réassortir (se vend, stock bas)' },
              { value: 'prix_desc', label: '💲 Prix décroissant' },
              { value: 'prix_asc', label: '💲 Prix croissant' },
              { value: 'nom_asc', label: '🔤 Nom (A→Z)' },
            ]}
          />
          <Select
            className="promo-tri" size="small" allowClear
            placeholder="Toutes les paires"
            value={promoFiltre || undefined}
            onChange={(v) => setPromoFiltre(v || '')}
            options={[
              { value: 'sans', label: '🆕 Pas en promo (à travailler)' },
              { value: 'expiree', label: '⌛ Promo expirée (à ressortir)' },
              { value: 'active', label: '🏷️ Déjà en promo' },
            ]}
          />
          {/* CE QUI NE SE VEND PAS — la question que se pose la propriétaire quand
              elle ouvre cet écran pour préparer une promotion. Mesuré en production
              le 22/08 : 107 modèles jamais vendus encore en stock depuis plus de six
              mois, et 56 974 $ de stock sans aucune vente sur 90 jours. */}
          <Select
            className="promo-tri" size="small" allowClear
            placeholder="Toutes les paires (aucun filtre)"
            value={decouverte || undefined}
            onChange={(v) => setDecouverte(v || '')}
            options={[
              { value: 'capital_mort', label: '🪦 Capital mort — jamais vendu, en stock, +6 mois' },
              { value: 'jamais_vendu', label: '🧊 Jamais vendu (et encore en stock)' },
              { value: 'dort', label: '💤 Dort en rayon — vendu avant, rien depuis 90 j' },
              { value: 'immobile', label: '🚫 Ne bouge pas — aucune vente sur 90 j' },
              { value: 'surstock', label: '📚 Surstock — 5 paires ou plus, rien vendu' },
              { value: 'vieux_stock', label: '🕰️ Vieux stock — arrivé il y a +6 mois' },
              { value: 'arrivage_recent', label: '📦 Arrivage récent (30 j) — à ne pas brader' },
              { value: 'vendu_recent', label: '🔥 Vendu récemment (30 j) — à ne pas brader' },
            ]}
          />
          <Select
            className="promo-tri" size="small" allowClear showSearch optionFilterProp="label"
            placeholder="Toutes les catégories"
            value={categorie || undefined}
            onChange={(v) => setCategorie(v || '')}
            options={categories.map((c) => ({ value: String(c.id_categorie), label: c.nom_categorie }))}
          />
          {chargeModeles && <Spin className="promo-spin" />}
          <div className="promo-resultats">
            {resultats.map((m) => {
              const pris = lot.some((x) => x.id_produit === m.id_produit);
              return (
                <div key={m.id_produit} className={`promo-resultat ${pris ? 'est-pris' : ''}`}>
                  <Checkbox checked={pris} onChange={() => basculerLot(m)} />
                  {/* Le nom ouvre le modèle SEUL (choix des variantes, pointures) ;
                      la case l'ajoute au lot (tout le modèle). Deux gestes
                      distincts, parce que ce sont deux intentions distinctes. */}
                  <button type="button" className="promo-resultat-ouvre" onClick={() => setModele(m)}>
                    <NdoeImage src={m.img} alt={m.nom_produit} size="sm" preview={false} />
                    <span className="promo-resultat-nom">
                      {m.nom_produit}
                      <small>{m.vendu90 > 0 ? `${m.vendu90} vendu(s) sur 90 j · ` : ''}{m.dispo} en stock</small>
                      {/* L etat promo, VISIBLE : sans lui on ne distingue pas
                          « jamais en promo » de « promo finie », et le filtre
                          agirait sans qu on puisse verifier ce qu il a fait. */}
                      {m.promoEtat && (
                        <Tag color={m.promoEtat === 'expiree' ? 'default' : 'magenta'} style={{ marginTop: 3 }}>
                          {ETAT_LABEL[m.promoEtat] || m.promoEtat}
                        </Tag>
                      )}
                    </span>
                    <span className="num">{Math.round(Number(m.prix))} $</span>
                  </button>
                </div>
              );
            })}
            {!chargeModeles && !resultats.length && (
              <p className="promo-vide">
                {decouverte || categorie || promoFiltre
                  ? 'Aucun modèle ne correspond à ces filtres.'
                  : 'Aucun modèle.'}
              </p>
            )}
            {/* NE JAMAIS COUPER EN SILENCE : la requête ne rend que 300 variantes et
                l'écran n'affiche que 60 modèles. Sans cette ligne, on croit avoir vu
                tout ce qui existe — et on rate justement les paires à écouler. */}
            {!chargeModeles && tronquee && (
              <p className="promo-tronque">
                60 premiers modèles affichés — affinez avec un filtre ou la recherche pour voir les autres.
              </p>
            )}
          </div>

          {/* Le lot : remise saisie UNE fois, aperçu POUR CHAQUE modèle — un
              « -20 % » ne donne pas le même montant sur 90 $ et sur 150 $. */}
          {lot.length > 0 && (
            <div className="promo-lot">
              <div className="promo-remise">
                <Segmented options={TYPES} value={type} onChange={setType} />
                <InputNumber inputMode="decimal" min={1} value={valeur} onChange={setValeur} className="num"
                  placeholder={type === 'pourcentage' ? '20' : type === 'montant' ? '15' : '89'}
                  addonAfter={type === 'pourcentage' ? '%' : '$'} />
              </div>
              <div className="promo-lot-liste">
                {apercuLot.map((m) => (
                  <div key={m.id_produit} className={`promo-lot-ligne ${m.promo == null && valeur ? 'is-invalide' : ''}`}>
                    <span className="promo-lot-nom">{m.nom_produit}</span>
                    <span className="num">
                      <s>{Math.round(Number(m.prix))} $</s>
                      {' → '}
                      {m.promo != null ? <b>{m.promo} $</b> : <em>hors limites</em>}
                    </span>
                    <button type="button" className="promo-lien" onClick={() => basculerLot(m)}>retirer</button>
                  </div>
                ))}
              </div>
              <label className="promo-switch">
                <Switch checked={permanente} onChange={setPermanente} />
                Permanente (sans dates)
              </label>
              {!permanente && (
                <div className="promo-dates">
                  Du <DatePicker value={debut} onChange={setDebut} format="YYYY-MM-DD" />
                  au <DatePicker value={fin} onChange={setFin} format="YYYY-MM-DD" />
                </div>
              )}
              <Input placeholder="Libellé commun (optionnel — généré sinon)" value={libelle} maxLength={120}
                onChange={(e) => setLibelle(e.target.value)} className="promo-libelle" />
              <NdoeActionButton variant="primary" block loading={envoi} onClick={() => enregistrerLot(false)}>
                Appliquer à {lot.length} modèle{lot.length > 1 ? 's' : ''}
              </NdoeActionButton>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="promo-modele-choisi">
            <NdoeImage src={modele.img} alt={modele.nom_produit} size="md" preview={false} />
            <div>
              <b>{modele.nom_produit}</b>
              <small>{modele.code_produit || ''} · prix actuel <span className="num">{Math.round(Number(modele.prix))} $</span></small>
            </div>
            {!edition && <button type="button" className="promo-lien" onClick={() => { setModele(null); setCochees(new Set()); }}>changer</button>}
          </div>

          {/* Geste 2 : la cible */}
          <div className="promo-geste">
            <b>2. Cible</b>
            <label className="promo-switch">
              <Switch checked={toutModele} onChange={(v) => setToutModele(v)} />
              Tout le modèle{!toutModele && ' — ou cochez des variantes :'}
            </label>
            {!toutModele && (
              <div className="promo-variantes">
                {Object.entries(parCouleur).map(([couleur, liste]) => {
                  const toutes = liste.every((v) => cochees.has(v.id_varianteProduit));
                  return (
                    <div key={couleur} className="promo-couleur">
                      <button type="button" className="promo-lien" onClick={() => cocherGroupe(liste, !toutes)}>
                        {toutes ? 'Décocher' : 'Toute la couleur'} « {couleur} »
                      </button>
                      <div className="promo-tailles">
                        {liste.map((v) => (
                          <Checkbox key={v.id_varianteProduit} checked={cochees.has(v.id_varianteProduit)}
                            onChange={() => basculerVariante(v.id_varianteProduit)}>
                            P{v.taille ?? '?'}
                          </Checkbox>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Geste 3 : la remise */}
          <div className="promo-geste">
            <b>3. Remise</b>
            <div className="promo-remise">
              <Segmented options={TYPES} value={type} onChange={setType} />
              <InputNumber inputMode="decimal" min={1} value={valeur} onChange={setValeur} className="num"
                placeholder={type === 'pourcentage' ? '20' : type === 'montant' ? '15' : '89'}
                addonAfter={type === 'pourcentage' ? '%' : '$'} />
              {prixPromo != null
                ? <span className="promo-apercu"><s className="num">{Math.round(Number(modele.prix))} $</s> → <b className="num">{prixPromo} $</b></span>
                : valeur ? <span className="promo-apercu is-invalide">valeur invalide</span> : null}
            </div>
            <label className="promo-switch">
              <Switch checked={permanente} onChange={setPermanente} />
              Permanente (sans dates)
            </label>
            {!permanente && (
              <div className="promo-dates">
                Du <DatePicker value={debut} onChange={setDebut} format="YYYY-MM-DD" />
                au <DatePicker value={fin} onChange={setFin} format="YYYY-MM-DD" />
              </div>
            )}
            <Input placeholder="Libellé (optionnel — généré sinon)" value={libelle} maxLength={120}
              onChange={(e) => setLibelle(e.target.value)} className="promo-libelle" />
          </div>

          <NdoeActionButton variant="primary" block loading={envoi} onClick={() => enregistrer(false)}>
            {edition ? 'Enregistrer l\'ajustement' : 'Enregistrer la promotion'}
          </NdoeActionButton>
        </>
      )}
    </Modal>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Promotions() {
  const { ready, can } = usePermissions();
  const peutEncoder = can('promotions.encoder');
  const peutValider = can('promotions.valider');

  const [statut, setStatut] = useState('');
  const [q, setQ] = useState('');
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [form, setForm] = useState(null);            // null | {} (création) | promo (ajustement)
  const [selection, setSelection] = useState(new Set());
  const [action, setAction] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true); setErreur(null);
    try {
      const p = new URLSearchParams();
      if (statut) p.set('statut', statut);
      if (q.trim()) p.set('q', q.trim());
      const { data } = await axios.get(`${DOMAIN}/api/promotions?${p.toString()}`);
      setPromos(data?.promotions || []);
      setSelection(new Set());
    } catch (e) { setErreur(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  }, [statut, q]);
  useEffect(() => { if (peutEncoder) charger(); else setLoading(false); }, [charger, peutEncoder]);

  const enAttente = promos.filter((p) => p.statut === 'en_attente');
  const basculerSel = (id) => setSelection((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toutSelectionner = () => setSelection((s) => (s.size === enAttente.length ? new Set() : new Set(enAttente.map((p) => p.id_promotion))));

  const decider = async (ids, decision) => {
    if (!ids.length || action) return;
    const envoyer = async (motif) => {
      setAction(true);
      try {
        const { data } = await axios.put(`${DOMAIN}/api/promotions/decision`, { ids, decision, motif });
        toast.success(data?.message || 'Fait.');
        await charger();
      } catch (e) { toast.error(e.response?.data?.error || 'Impossible.'); }
      finally { setAction(false); }
    };
    if (decision === 'refuser') {
      let motif = '';
      Modal.confirm({
        title: `Refuser ${ids.length} promotion(s)`,
        content: <Input.TextArea rows={2} placeholder="Motif (obligatoire)" onChange={(e) => { motif = e.target.value; }} />,
        okText: 'Refuser', okButtonProps: { danger: true }, cancelText: 'Annuler',
        onOk: () => (String(motif).trim() ? envoyer(motif) : (toast.info('Motif obligatoire.'), Promise.reject())),
      });
    } else envoyer();
  };

  const desactiver = (p) => Modal.confirm({
    title: `Désactiver « ${p.libelle} » ?`,
    content: 'Le prix normal reprend immédiatement.',
    okText: 'Désactiver', cancelText: 'Annuler',
    onOk: async () => {
      try {
        await axios.put(`${DOMAIN}/api/promotions/${p.id_promotion}/desactiver`);
        toast.success('Promotion désactivée.');
        await charger();
      } catch (e) { toast.error(e.response?.data?.error || 'Impossible.'); }
    },
  });

  if (ready && !peutEncoder) {
    return (
      <NdoePage className="promo-page">
        <NdoePageHeader eyebrow="Catalogue" title="Promotions" />
        <NdoeEmpty icon="🔒" title="Accès non accordé"
          text="Demandez à l'administrateur d'activer « Encoder / ajuster une promotion » pour votre compte (matrice des permissions)." />
      </NdoePage>
    );
  }

  return (
    <NdoePage className="promo-page">
      <NdoePageHeader
        eyebrow="Catalogue"
        title="Promotions"
        subtitle="Prix barré → prix promo, sur un modèle entier ou des variantes précises. Sans dates, la promotion est permanente."
        actions={<NdoeActionButton variant="primary" icon={<PlusOutlined />} onClick={() => setForm({})}>Nouvelle promotion</NdoeActionButton>}
      />

      <div className="promo-filtres">
        <Input allowClear prefix={<SearchOutlined />} placeholder="Modèle, référence ou libellé…"
          value={q} onChange={(e) => setQ(e.target.value)} className="promo-f-q" />
        <Select allowClear placeholder="Statut" value={statut || undefined} onChange={(v) => setStatut(v || '')}
          options={Object.entries(STATUT_TAG).map(([value, s]) => ({ value, label: s.label }))} className="promo-f" />
      </div>

      {peutValider && enAttente.length > 0 && (
        <div className="promo-validation-barre">
          <Checkbox checked={selection.size === enAttente.length && enAttente.length > 0}
            indeterminate={selection.size > 0 && selection.size < enAttente.length}
            onChange={toutSelectionner}>
            Tout sélectionner ({enAttente.length} en attente)
          </Checkbox>
          <div className="promo-validation-actions">
            <NdoeActionButton size="sm" variant="primary" icon={<CheckOutlined />} disabled={!selection.size || action}
              onClick={() => decider([...selection], 'activer')}>Activer la sélection ({selection.size})</NdoeActionButton>
            <NdoeActionButton size="sm" icon={<CloseOutlined />} disabled={!selection.size || action}
              onClick={() => decider([...selection], 'refuser')}>Refuser</NdoeActionButton>
          </div>
        </div>
      )}

      {loading ? <NdoeLoading rows={4} />
        : erreur ? <NdoeError text={erreur} onRetry={charger} />
        : !promos.length ? <NdoeEmpty icon="🏷️" title="Aucune promotion" text="Créez la première avec « Nouvelle promotion »." />
        : (
          <div className="promo-liste">
            {promos.map((p) => (
              <div key={p.id_promotion} className={`promo-ligne is-${p.statut}`}>
                {peutValider && p.statut === 'en_attente' && (
                  <Checkbox checked={selection.has(p.id_promotion)} onChange={() => basculerSel(p.id_promotion)} />
                )}
                <NdoeImage src={p.img} alt={p.nom_produit} size="sm" preview={false} />
                <div className="promo-ligne-corps">
                  <b>{p.libelle}</b>
                  <small>{p.nom_produit} · {p.cible}{p.nb_variantes > 0 && p.variantes.length
                    ? ` (${p.variantes.map((v) => `P${v.taille ?? '?'}`).join(', ')})` : ''}</small>
                  <small>
                    <s className="num">{p.prix_base} $</s> → <b className="num">{p.prix_promo ?? '—'} $</b>
                    {' · '}{p.etat_temporel === 'permanente' ? 'Permanente' : `${p.date_debut || '…'} → ${p.date_fin || '…'} (${ETAT_LABEL[p.etat_temporel]})`}
                    {p.encodeur ? ` · par ${p.encodeur}` : ''}
                  </small>
                  {p.statut === 'refusee' && p.motif_refus && <small className="promo-refus">Refusée : {p.motif_refus}</small>}
                </div>
                <Tag color={STATUT_TAG[p.statut].color}>{STATUT_TAG[p.statut].label}</Tag>
                <div className="promo-ligne-actions">
                  {peutValider && p.statut === 'en_attente' && (
                    <>
                      <Tooltip title="Activer"><button type="button" className="promo-btn is-ok" disabled={action} onClick={() => decider([p.id_promotion], 'activer')}><CheckOutlined /></button></Tooltip>
                      <Tooltip title="Refuser"><button type="button" className="promo-btn is-ko" disabled={action} onClick={() => decider([p.id_promotion], 'refuser')}><CloseOutlined /></button></Tooltip>
                    </>
                  )}
                  {['en_attente', 'active', 'refusee'].includes(p.statut) && (
                    <Tooltip title="Ajuster"><button type="button" className="promo-btn" onClick={() => setForm(p)}><EditOutlined /></button></Tooltip>
                  )}
                  {p.statut === 'active' && (
                    <Tooltip title="Désactiver"><button type="button" className="promo-btn" onClick={() => desactiver(p)}><StopOutlined /></button></Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

      {form !== null && (
        <FormulairePromo promo={form.id_promotion ? form : null}
          onClose={() => setForm(null)}
          onSaved={() => { setForm(null); charger(); }} />
      )}
    </NdoePage>
  );
}
